import capitalize from "lodash/capitalize";

export function userInitials(user: {
  first_name: string | null;
  last_name: string | null;
}) {
  return user
    ? `${user.first_name?.charAt(0) ?? ""}${user.last_name?.charAt(0) ?? ""}`
    : "";
}

export function userFullName(user: {
  first_name: string | null;
  last_name: string | null;
}) {
  return user
    ? `${user.first_name ?? ""}${user.last_name ? " " + user.last_name : ""}`
    : "";
}

export function userDisplayName(user: {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}) {
  return user ? userFullName(user) || user.email : "";
}

export const parseStatusString = (s: string | null): string => {
  switch (s) {
    case "IN_PROGRESS":
      return "In Progress";
    case "TO_DO":
      return "To-do";
    case "NOT_PROCESSED":
      return "Not Processed";
    case null:
      return "";
    default:
      return capitalize(s);
  }
};

export const sortBySubsection = (
  a: { subsection_index: string | null },
  b: { subsection_index: string | null },
) => {
  const parseIndex = (index: string) => {
    // Split the index by '.' and then map each part
    return index.split(".").map((part) => {
      // Check if the part is numeric
      if (!isNaN(Number(part))) {
        // Return the number
        return { type: "number", value: Number(part) };
      } else {
        // Return the string
        return { type: "string", value: part };
      }
    });
  };

  const numA = a.subsection_index ? parseIndex(a.subsection_index) : [];
  const numB = b.subsection_index ? parseIndex(b.subsection_index) : [];

  const maxLength = Math.max(numA.length, numB.length);

  for (let i = 0; i < maxLength; i++) {
    const partA = numA[i] || { type: "number", value: 0 }; // Treat missing subsection as 0
    const partB = numB[i] || { type: "number", value: 0 };

    if (partA.type === partB.type) {
      // Both parts are of the same type (number or string)
      if (partA.value !== partB.value) {
        return partA.value < partB.value ? -1 : 1;
      }
    } else {
      // One part is a number, the other is a string
      // We consider numbers to come before strings
      return partA.type === "number" ? -1 : 1;
    }
  }

  // If subsections are the same or missing, don't sort
  return 0;
};

/**
 * Process rich text content by stripping HTML tags and cleaning up formatting
 *
 * @param content - The HTML content to process
 * @returns Clean text content with HTML tags removed and entities decoded
 */
export const processTextContent = (content: string): string => {
  if (!content) return "";
  const processedContent = content
    .replace(/<\/li>/gi, "\n") // add newlines after list items
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/\n\s*\n\s*\n/g, "\n\n")
    .trim();

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = processedContent;
  return tempDiv.textContent || tempDiv.innerText || "";
};

/**
 * Parses a string containing partner names and returns an array of clean names
 *
 * Handles various formats:
 * - Single names: "Mike Curry"
 * - Comma separated: "Mike Curry, Bob Dole, Steven Chen"
 * - Ampersand separated: "Mike Curry & Bob Dole"
 * - Semicolon separated: "Mike Curry; Bob Dole; Steven Chen"
 * - Mixed spacing: "Mike Curry , Bob Dole , Steven Chen"
 * - Names with parentheses: "Lindsay Kaplan (domestic/international)" -> "Lindsay Kaplan"
 * - Complex parentheses: "Lindsay Kaplan (domestic/international prosecution and clearance; international disputes)" -> "Lindsay Kaplan"
 * - Mixed formats: "Ashley Edmonds, Alice Danelia, and Isabel Triana (all DC)"
 *
 * @param partnerString - The raw string containing partner names
 * @returns Array of clean partner names with parentheses removed
 */
export const parsePartnerNames = (partnerString: string): string[] => {
  if (!partnerString || typeof partnerString !== "string") return [];

  // 1) Strip HTML and decode entities
  const cleanText = processTextContent(partnerString);

  // 2) Remove common list/prefix noise like: "1) ", "1. ", "As to all: "
  const normalized = cleanText
    .replace(/^\s*\d+\s*[).\]]\s*/i, "") // leading "1) " / "1." / "1]"
    .replace(/^\s*as\s+to\s+all:\s*/i, "") // "As to all: "
    .replace(/\s+/g, " ") // collapse whitespace
    .trim();

  // 3) Remove parenthetical notes
  const withoutParens = normalized.replace(/\s*\([^)]*\)/g, "").trim();

  // 4) Split on common delimiters including "and" and "&" when used as connectors
  //    Use word-boundary and to avoid splitting inside words.
  const tokens = withoutParens
    .split(/[,;]|(?:\s+\band\b\s+)|(?:\s*&\s*)/gi)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  // 5) Cleanup connectors and artifacts
  const names = tokens
    .map((partner) =>
      partner
        .replace(/^\s*(?:and|&)\s*/i, "")
        .replace(/\s*(?:and|&)\s*$/i, "")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .filter(
      (name) =>
        name.length > 1 && !["and", "or", "&"].includes(name.toLowerCase()),
    );

  return names;
};
